import { useEffect, useState } from "react";
import type { Bootstrap, Bill } from "../../shared/types";
import { api } from "../lib/api";
import {
  formatMoney, formatPeriod, formatDayMonth,
  shiftPeriod, daysBetween, todayISO, dueDateInCycle, formatDueDate,
} from "../lib/format";
import StatCard from "../components/StatCard";
import Select from "../components/Select";
import { IncomeIcon, OutgoingsIcon, PiggyIcon, ShieldIcon, WalletIcon, EditIcon, TrashIcon } from "../components/icons";
import BillModal from "../components/BillModal";
import IncomeModal from "../components/IncomeModal";
import BalanceModal from "../components/BalanceModal";
import PaydayModal from "../components/PaydayModal";
import SafeToSpendModal from "../components/SafeToSpendModal";
import BalancesModal from "../components/BalancesModal";
import ConfirmModal from "../components/ConfirmModal";
import { useConfirm } from "../components/ConfirmProvider";

type PayFilter = "all" | "topay" | "paid";

export default function ThisMonth({
  data,
  reload,
  period,
  setPeriod,
}: {
  data: Bootstrap | null;
  reload: () => Promise<void>;
  period: string;
  setPeriod: (p: string) => void;
}) {
  const [paidIds, setPaidIds] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<null | "balance" | "balances" | "income" | "bill" | "payday" | "safe">(null);
  const [editBill, setEditBill] = useState<Bill | null>(null);
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState<PayFilter>("all");
  const [person, setPerson] = useState<string>("both");
  const [sort, setSort] = useState<"due" | "amount" | "name">("due");
  // Confirmation for the bulk tick actions (true = mark all, false = reset).
  const [confirmAll, setConfirmAll] = useState<boolean | null>(null);
  const ask = useConfirm();

  useEffect(() => {
    if (data) setPaidIds(new Set(data.items.filter((b) => b.paid).map(keyOf)));
  }, [data]);

  if (!data) return <div className="state">Loading…</div>;

  const { summary, items, funds } = data;
  const typeColours: Record<string, string> = Object.fromEntries(data.types.map((t) => [t.key, t.colour]));

  async function toggle(bill: Bill) {
    const k = keyOf(bill);
    if (busyIds.has(k)) return;
    const nextPaid = !paidIds.has(k);
    setPaidIds((p) => withToggled(p, k, nextPaid));
    setBusyIds((p) => new Set(p).add(k));
    try {
      if (bill.planId != null && bill.seq != null) await api.tickPlan(bill.planId, bill.seq, nextPaid);
      else await api.tick(bill.id, period, nextPaid, bill.occ ?? "");
      await reload();
    } catch {
      setPaidIds((p) => withToggled(p, k, !nextPaid));
    } finally {
      setBusyIds((p) => { const n = new Set(p); n.delete(k); return n; });
    }
  }

  async function markAll(paid: boolean) {
    setPaidIds(paid ? new Set(items.map(keyOf)) : new Set());
    try {
      await api.tickAll(period, paid);
      await Promise.all(
        items.filter((i) => i.planId != null).map((i) => api.tickPlan(i.planId!, i.seq!, paid)),
      );
      await reload();
    } catch {
      await reload();
    }
  }

  async function removeBill(bill: Bill) {
    if (!(await ask({ title: "Archive bill?", message: `Archive "${bill.name}"? It stops counting but stays in your history — you can restore it from Recurring.`, confirmLabel: "Archive" }))) return;
    await api.deleteBill(bill.id);
    await reload();
  }

  const total = summary.totalOutgoingsMinor;
  const paidSoFar = items.filter((b) => paidIds.has(keyOf(b))).reduce((t, b) => t + b.amountMinor, 0);
  const leftToPay = total - paidSoFar;
  const itemsPaid = items.filter((b) => paidIds.has(keyOf(b))).length;
  const paidPct = total > 0 ? Math.round((paidSoFar / total) * 100) : 0;

  // Safe to spend: funds spread evenly over the days left in the pay cycle
  // (payday -> next payday). "Per week" never exceeds the days actually left.
  const today = todayISO();
  const inThisCycle = today >= summary.cycleStart && today <= summary.cycleEnd;
  // cycleEnd is inclusive, so days remaining includes today (and the payday next).
  const daysLeft = inThisCycle
    ? daysBetween(today, summary.cycleEnd) + 1
    : daysBetween(summary.cycleStart, summary.cycleEnd) + 1;
  const todayLabel = new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const safe = funds.safeToSpendMinor;
  const perDay = safe != null && daysLeft > 0 ? Math.round(safe / daysLeft) : null;
  const perWeek = perDay != null ? perDay * Math.min(7, daysLeft) : null;
  // Cycle length — a payday-to-payday month is naturally 4 or 5 weeks.
  const cycleWeeks = Math.round((daysBetween(summary.cycleStart, summary.cycleEnd) + 1) / 7);

  const shown = items
    .filter((b) => (person === "both" ? true : b.paidByName === person || (b.planId != null && b.paidByInitial === person[0])))
    .filter((b) => (payFilter === "all" ? true : payFilter === "paid" ? paidIds.has(keyOf(b)) : !paidIds.has(keyOf(b))))
    .filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      if (sort === "amount") return b.amountMinor - a.amountMinor;
      if (sort === "name") return a.name.localeCompare(b.name);
      // Due date: order by each occurrence's ACTUAL date in the cycle, so bills
      // due just after payday sit above next month's day-1s, and a second charge
      // in a 5-week cycle lands at its real late-month position.
      const da = a.dueDate ?? dueDateInCycle(a.dueDay, summary.cycleStart, summary.cycleEnd) ?? "9999-12-31";
      const db = b.dueDate ?? dueDateInCycle(b.dueDay, summary.cycleStart, summary.cycleEnd) ?? "9999-12-31";
      return da < db ? -1 : da > db ? 1 : 0;
    });

  return (
    <div className="page">
      <p className="page__eyebrow">Monthly position</p>
      <div className="month-top">
        <div className="month-nav mt-nav">
          <button className="month-nav__btn" onClick={() => setPeriod(shiftPeriod(period, -1))} aria-label="Previous month">‹</button>
          <h1 className="month-nav__title">{formatPeriod(period)}</h1>
          <button className="month-nav__btn" onClick={() => setPeriod(shiftPeriod(period, 1))} aria-label="Next month">›</button>
          <span className="month-today">Today · {todayLabel}</span>
        </div>
        <div className="month-actions mt-actions">
          <button className="btn btn--ghost" onClick={() => setModal("balances")}>Balance</button>
          <button className="btn btn--ghost" onClick={() => setModal("income")}>Income</button>
          <button className="btn btn--primary" onClick={() => { setEditBill(null); setModal("bill"); }}>+ Add bill</button>
        </div>

        <button className="cycle-chip mt-cycle" onClick={() => setModal("payday")}>
          Pay cycle: {formatDayMonth(summary.cycleStart)} – {formatDayMonth(summary.cycleEnd)}
          <span className={"week-badge week-badge--" + cycleWeeks}>{cycleWeeks}-week</span>
          {!summary.cycleSet && <span className="cycle-chip__auto">auto</span>}
          <span className="cycle-chip__edit" aria-hidden="true">✎</span>
        </button>

        <section className="hero-card mt-hero">
          <p className="hero-card__label">Left to pay this month</p>
          <p className="hero-card__figure">{formatMoney(leftToPay)}</p>
          <p className="hero-card__meta">of {formatMoney(total)} total · {itemsPaid}/{summary.itemsTotal} items paid</p>
          <div className="hero-card__bar"><span style={{ width: `${paidPct}%` }} /></div>
          <div className="hero-card__bar-labels">
            <span>{formatMoney(paidSoFar)} paid</span>
            <span>{paidPct}% paid</span>
          </div>
        </section>
        <div className="tile-quad tile-quad--stack mt-quad">
          <StatCard label="Household Salary" value={formatMoney(summary.incomeReceivedMinor)} sub={data.household.members.map((m) => m.name).join(" & ")} icon={<IncomeIcon />} />
          <StatCard label="Total outgoings" value={formatMoney(summary.totalOutgoingsMinor)} sub={summary.planPaymentCount > 0 ? `${summary.billCount} bills · ${summary.planPaymentCount} plan payments` : `${summary.billCount} bills`} icon={<OutgoingsIcon />} />
        </div>

        <div className={"tile-row3 mt-row3" + (inThisCycle ? "" : " tile-row3--2")}>
        {inThisCycle ? (
          // Current cycle: "money in your account now, minus bills still to pay".
          <button type="button" className="stat-card stat-card--accent stat-card--tap" onClick={() => setModal("safe")}>
            <div className="stat-card__top">
              <p className="stat-card__label">Safe to spend</p>
              <span className="stat-card__icon" aria-hidden="true"><ShieldIcon /></span>
            </div>
            <p className={"stat-card__value" + (safe != null && safe < 0 ? " stat-card__value--negative" : safe != null ? " stat-card__value--positive" : "")}>
              {safe != null ? formatMoney(safe) : "—"}
            </p>
            <p className="stat-card__sub">
              {perDay != null ? `${formatMoney(perDay)}/day · ${formatMoney(perWeek!)}/wk · ` : safe == null ? "Set your balance · " : ""}
              {`${daysLeft} day${daysLeft === 1 ? "" : "s"} to payday`}
              <span className="stat-card__more">Weekly breakdown ›</span>
            </p>
          </button>
        ) : (
          // Other months: "Safe to spend" is a right-now figure and doesn't apply.
          // Show the month's projection instead (income − all its bills).
          <StatCard
            label="Left after everything"
            value={formatMoney(summary.leftAfterEverythingMinor)}
            sub={`Projected · salary ${formatMoney(summary.incomeReceivedMinor)} − bills ${formatMoney(summary.totalOutgoingsMinor)}`}
            accent
            tone={summary.leftAfterEverythingMinor >= 0 ? "positive" : "negative"}
            icon={<PiggyIcon />}
          />
        )}
        <div className="stat-card">
          <div className="stat-card__row">
            <span className="stat-card__labelwrap">
              <span className="stat-card__icon" aria-hidden="true"><WalletIcon /></span>
              <p className="stat-card__label">Funds remaining{inThisCycle ? "" : " (today)"}</p>
            </span>
            <button className="btn btn--ghost btn--sm" onClick={() => setModal("balance")}>Update</button>
          </div>
          <p className="stat-card__value">{funds.fundsRemainingMinor != null ? formatMoney(funds.fundsRemainingMinor) : "—"}</p>
          <p className="stat-card__sub">
            {funds.asOf == null
              ? "Set your balance to track"
              : funds.carriedSalaryMinor > 0
                ? `Est. — incl. ${formatMoney(funds.carriedSalaryMinor)} salary this cycle · update to confirm`
                : `Balance ${formatMoney(funds.combinedMinor ?? 0)} on ${formatDayMonth(funds.asOf)}`}
          </p>
        </div>
        {inThisCycle && (
          <StatCard label="Left after everything" value={formatMoney(summary.leftAfterEverythingMinor)} sub="Free to save or spend" accent tone={summary.leftAfterEverythingMinor >= 0 ? "positive" : "negative"} icon={<PiggyIcon />} />
        )}
        </div>
      </div>

      <section className="bills-panel">
        <div className="bills-panel__head">
          <div className="bills-panel__title">
            <h3>Bills &amp; outgoings</h3>
            <span className="badge">{shown.length} shown</span>
          </div>
          <div className="bills-panel__actions">
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmAll(true)}>Mark all paid</button>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmAll(false)}>Reset ticks</button>
          </div>
        </div>

        <div className="bills-toolbar">
          <input className="input bills-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Segmented value={payFilter} onChange={(v) => setPayFilter(v as PayFilter)} options={[["all", "All"], ["topay", "To pay"], ["paid", "Paid"]]} />
          <Segmented value={person} onChange={setPerson} options={[["both", "Both"], ...data.household.members.map((m) => [m.name, m.name] as [string, string])]} />
          <div className="bills-sort">
            <Select
              value={sort}
              onChange={(v) => setSort(v as "due" | "amount" | "name")}
              ariaLabel="Sort bills"
              options={[
                { value: "due", label: "Sort: Due date" },
                { value: "amount", label: "Sort: Amount" },
                { value: "name", label: "Sort: Name" },
              ]}
            />
          </div>
        </div>

        <div className="bill-table__head">
          <span>Bill</span><span className="ta-c">By</span><span className="ta-c">Due date</span><span className="ta-r">Amount</span><span />
        </div>
        {shown.length === 0 && <div className="empty">Nothing matches these filters.</div>}
        {shown.map((b) => (
          <BillRow
            key={keyOf(b)}
            bill={b}
            cycleStart={summary.cycleStart}
            cycleEnd={summary.cycleEnd}
            typeColours={typeColours}
            paid={paidIds.has(keyOf(b))}
            busy={busyIds.has(keyOf(b))}
            onToggle={() => toggle(b)}
            onEdit={() => { setEditBill(b); setModal("bill"); }}
            onDelete={() => removeBill(b)}
          />
        ))}
        {shown.length > 0 && (
          <div className="table-total table-total--flush">
            <span className="table-total__label">Total{shown.length !== items.length ? " (filtered)" : ""}</span>
            <span className="table-total__value">{formatMoney(shown.reduce((t, b) => t + b.amountMinor, 0))}</span>
          </div>
        )}
      </section>

      {modal === "balance" && (
        <BalanceModal data={data} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await reload(); }} />
      )}
      {modal === "income" && (
        <IncomeModal data={data} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await reload(); }} />
      )}
      {modal === "bill" && (
        <BillModal data={data} bill={editBill} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await reload(); }} />
      )}
      {modal === "payday" && (
        <PaydayModal
          period={period}
          start={summary.cycleStart}
          end={summary.cycleEnd}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await reload(); }}
        />
      )}
      {modal === "safe" && data && (
        <SafeToSpendModal data={data} period={period} onClose={() => setModal(null)} />
      )}
      {modal === "balances" && (
        <BalancesModal onClose={() => setModal(null)} onUpdate={() => setModal("balance")} />
      )}
      {confirmAll !== null && (
        <ConfirmModal
          title={confirmAll ? "Mark all bills paid?" : "Reset all ticks?"}
          message={confirmAll
            ? "This ticks every bill in this cycle as paid. You can still untick them one by one afterwards."
            : "This clears the paid tick on every bill in this cycle. Nothing is deleted — you can re-tick them."}
          confirmLabel={confirmAll ? "Yes, mark all paid" : "Yes, reset ticks"}
          danger={!confirmAll}
          onConfirm={() => { const paid = confirmAll; setConfirmAll(null); markAll(paid); }}
          onClose={() => setConfirmAll(null)}
        />
      )}
    </div>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="segmented">
      {options.map(([val, label]) => (
        <button key={val} className={"segmented__btn" + (value === val ? " is-active" : "")} onClick={() => onChange(val)}>{label}</button>
      ))}
    </div>
  );
}

function withToggled(set: Set<string>, id: string, on: boolean): Set<string> {
  const next = new Set(set);
  if (on) next.add(id); else next.delete(id);
  return next;
}

// A unique key per row: bills key by id + occurrence (a 5-week cycle can list a
// bill twice); plan instalments already have unique (negative) ids.
function keyOf(b: Bill): string {
  return b.planId != null ? `p${b.id}` : `b${b.id}:${b.occ ?? ""}`;
}

function BillRow({
  bill, cycleStart, cycleEnd, typeColours, paid, busy, onToggle, onEdit, onDelete,
}: {
  bill: Bill; cycleStart: string; cycleEnd: string; typeColours: Record<string, string>; paid: boolean; busy: boolean;
  onToggle: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const tagColour = bill.provider ? (typeColours.plan ?? "#7F56D9") : (typeColours[bill.type] ?? "#667085");
  const dueIso = bill.dueDate ?? dueDateInCycle(bill.dueDay, cycleStart, cycleEnd);
  return (
    <div className={"bill-table__row" + (paid ? " is-paid" : "")}>
      <div className="bill-table__bill">
        <input type="checkbox" className="bill-row__check" checked={paid} disabled={busy} onChange={onToggle} aria-label={`Mark ${bill.name} paid`} />
        <div className="bill-row__main">
          <span className="bill-row__name">{bill.name}</span>
          <span className="bill-row__tags">
            {bill.categoryName && (
              <span className="chip"><span className="chip__dot" style={{ background: bill.categoryColour ?? "#98A2B3" }} />{bill.categoryName}</span>
            )}
            {bill.provider ? (
              <span className="type-tag" style={{ background: tagColour + "22", color: tagColour }}>{bill.provider}</span>
            ) : bill.type ? (
              <span className="type-tag" style={{ background: tagColour + "22", color: tagColour }}>{bill.type}</span>
            ) : null}
          </span>
        </div>
      </div>
      <div className="ta-c">
        {bill.paidByInitial && <span className="avatar" style={{ background: bill.paidByColour ?? "#98A2B3" }} title={bill.paidByName ?? ""}>{bill.paidByInitial}</span>}
      </div>
      <div className="ta-c bill-table__due">{dueIso ? formatDueDate(dueIso) : "—"}</div>
      <div className="ta-r bill-row__amount">{formatMoney(bill.amountMinor)}</div>
      <div className="bill-table__icons">
        {bill.planId == null ? (
          <>
            <button className="icon-btn" onClick={onEdit} aria-label="Edit" title="Edit">
              <EditIcon />
            </button>
            <button className="icon-btn" onClick={onDelete} aria-label="Delete" title="Delete">
              <TrashIcon />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

